import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dashboardApi, DashboardGraphData, DashboardStats } from '../../api/dashboard';
import { PageHeader, StatCard, CardsSkeleton } from '../../components/ui';
import { formatCurrency, formatDate, initials } from '../../utils';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2];

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [graph, setGraph] = useState<DashboardGraphData | null>(null);
  const [year, setYear] = useState(String(currentYear));

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    dashboardApi
      .getStats()
      .then((data) => {
        if (active) setStats(data);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load dashboard stats');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setGraphLoading(true);
    dashboardApi
      .getGraphData(year)
      .then((data) => {
        if (active) setGraph(data);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load graph data');
      })
      .finally(() => {
        if (active) setGraphLoading(false);
      });
    return () => {
      active = false;
    };
  }, [year]);

  const chartData = useMemo(() => {
    if (!graph) return [];
    return MONTHS.map((month, index) => ({
      month,
      [`${graph.year}`]: graph.usersThisYear[index] || 0,
      [`${graph.previousYear}`]: graph.usersPreviousYear[index] || 0,
    }));
  }, [graph]);

  if (loading && !stats) {
    return (
      <Box>
        <PageHeader title="Dashboard" subtitle="Earnings, latest users, and registration trends" />
        <CardsSkeleton count={3} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Dashboard" subtitle="Earnings, latest users, and registration trends" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Earnings
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Today"
            value={formatCurrency(stats?.amountToday || 0)}
            icon={<PaymentsIcon />}
            accent="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="This Week"
            value={formatCurrency(stats?.amountThisWeek || 0)}
            icon={<PaymentsIcon />}
            accent="#1E3A8A"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="This Month"
            value={formatCurrency(stats?.amountThisMonth || 0)}
            icon={<PaymentsIcon />}
            accent="#F59E0B"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h3">User Registrations</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="dashboard-year-label">Year</InputLabel>
          <Select
            labelId="dashboard-year-label"
            label="Year"
            value={year}
            onChange={(e) => setYear(String(e.target.value))}
          >
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, height: 320 }}>
        {graphLoading && !graph ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading chart…</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {graph && (
                <>
                  <Line
                    type="monotone"
                    dataKey={`${graph.year}`}
                    name={`${graph.year}`}
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey={`${graph.previousYear}`}
                    name={`${graph.previousYear}`}
                    stroke="#1E3A8A"
                    strokeWidth={2}
                    dot={false}
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Latest Users
      </Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(stats?.latestUsers || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No recent users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              stats?.latestUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar src={user.profilePicture || undefined} sx={{ width: 32, height: 32 }}>
                        {initials(user.name || user.email || 'U')}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>
                        {user.name || '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone || '—'}</TableCell>
                  <TableCell>{user.createdAt ? formatDate(user.createdAt) : '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default DashboardPage;
